import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { ArrowUp, MessageCircle, X, Phone, Mail } from "lucide-react";
import { company } from "@/data/site";
import { cn } from "@/lib/utils";

/* Scroll progress bar fixed at top */
export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  return (
    <motion.div
      style={{ scaleX }}
      className="fixed left-0 top-0 z-[60] h-[3px] w-full origin-left bg-gradient-to-r from-primary via-accent to-primary"
      data-testid="scroll-progress"
    />
  );
}

/* Back to top */
export function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.6 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 left-6 z-50 grid h-11 w-11 place-items-center rounded-full border border-border bg-card text-foreground shadow-lg transition-colors hover:bg-primary hover:text-primary-foreground"
          data-testid="back-to-top"
          aria-label="Back to top"
        >
          <ArrowUp className="h-5 w-5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

/* Floating contact / WhatsApp cluster */
export function FloatingContact() {
  const [open, setOpen] = useState(false);
  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            className="flex flex-col gap-2"
          >
            <a
              href={`https://wa.me/${company.whatsapp}?text=${encodeURIComponent(company.whatsappMsg)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-button"
              className="flex items-center gap-3 rounded-full bg-[#25D366] px-4 py-3 text-sm font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <a
              href={`tel:${company.phoneHref}`}
              data-testid="floating-call"
              className="flex items-center gap-3 rounded-full bg-card px-4 py-3 text-sm font-medium text-foreground shadow-lg ring-1 ring-border transition-transform hover:-translate-y-0.5"
            >
              <Phone className="h-4 w-4 text-primary" /> Call us
            </a>
            <a
              href={`mailto:${company.email}`}
              data-testid="floating-email"
              className="flex items-center gap-3 rounded-full bg-card px-4 py-3 text-sm font-medium text-foreground shadow-lg ring-1 ring-border transition-transform hover:-translate-y-0.5"
            >
              <Mail className="h-4 w-4 text-primary" /> Email
            </a>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="floating-contact-toggle"
        aria-label="Contact options"
        className={cn(
          "grid h-14 w-14 place-items-center rounded-full text-white shadow-xl transition-colors",
          open ? "bg-secondary" : "bg-primary"
        )}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X className="h-6 w-6" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle className="h-6 w-6" />
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
}

/* Cookie banner */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!localStorage.getItem("ntq-cookie")) setVisible(true);
    }, 1500);
    return () => clearTimeout(t);
  }, []);
  const accept = () => {
    localStorage.setItem("ntq-cookie", "accepted");
    setVisible(false);
  };
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-4 bottom-4 z-[55] mx-auto max-w-3xl rounded-2xl border border-border bg-card p-5 shadow-2xl sm:flex sm:items-center sm:justify-between sm:gap-6"
          data-testid="cookie-banner"
        >
          <p className="text-sm text-muted-foreground">
            We use cookies to improve your experience and analyze traffic. By continuing, you agree to our use of cookies.
          </p>
          <div className="mt-4 flex gap-3 sm:mt-0 sm:shrink-0">
            <button
              onClick={() => setVisible(false)}
              className="rounded-full px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              data-testid="cookie-decline"
            >
              Decline
            </button>
            <button
              onClick={accept}
              className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
              data-testid="cookie-accept"
            >
              Accept
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* Brand loading screen */
export function LoadingScreen() {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDone(true), 1400);
    return () => clearTimeout(t);
  }, []);
  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="fixed inset-0 z-[100] grid place-items-center bg-[#020617]"
          data-testid="loading-screen"
        >
          <div className="flex flex-col items-center gap-6">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center gap-4"
            >
              <img src="/logo.jpg" alt="NeuroTriq TechLabs" className="h-24 w-24 rounded-2xl bg-white object-contain p-1 shadow-xl" />
              <span className="font-display text-2xl font-semibold tracking-tight text-white">NeuroTriq TechLabs</span>
            </motion.div>
            <div className="h-[3px] w-40 overflow-hidden rounded-full bg-white/10">
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: "0%" }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="h-full w-full bg-gradient-to-r from-primary to-accent"
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
